import { AnexoOrientacaoCompleta, CampoOrientacaoCompleta } from "./types";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";

const CAMPOS_ORDEM: Array<{ key: string; label: string }> = [
    { key: "parte_representada", label: "Qual parte representamos" },
    { key: "nome_advogado", label: "Nome do advogado" },
    { key: "oab_advogado", label: "OAB do advogado" },
    { key: "nome_preposto", label: "Nome do preposto" },
    { key: "proposta_acordo", label: "Proposta de acordo" },
    { key: "requerimentos", label: "Requerimentos a realizar em audiência" },
    { key: "documentos", label: "Documentos a juntar" },
    { key: "testemunhas", label: "Testemunhas a ouvir" },
    { key: "pedidos", label: "Pedidos relevantes da inicial" },
    { key: "resumo_contestacao", label: "Resumo da contestação" },
    { key: "outras_informacoes", label: "Outras informações" },
    { key: "status", label: "Status da orientação" }
];

const labelPorChave: Record<string, string> = CAMPOS_ORDEM.reduce((acc, item) => {
    acc[item.key] = item.label;
    return acc;
}, {} as Record<string, string>);

const formatarValor = (valor: any): string => {
    if (valor === null || valor === undefined) {
        return "—";
    }

    if (typeof valor === "number") {
        return String(valor);
    }

    if (typeof valor === "string") {
        const texto = valor.trim();
        return texto.length > 0 ? texto : "—";
    }

    return String(valor);
};

const extrairNomeArquivo = (valor?: string | null): string => {
    if (!valor) {
        return "Arquivo sem nome";
    }

    const partes = valor.split("/");
    const nome = partes[partes.length - 1];

    return nome && nome.trim().length > 0 ? nome : valor;
};

const formatarTamanho = (tamanho?: number | string | null): string => {
    if (tamanho === null || tamanho === undefined) {
        return "—";
    }

    if (typeof tamanho === 'string' && /^[\d.]+\s*(B|KB|MB|GB)$/.test(tamanho.trim())) {
        return tamanho;
    }

    const valorNumerico = Number(tamanho);

    if (Number.isNaN(valorNumerico) || valorNumerico <= 0) {
        return "—";
    }

    const unidades = ["B", "KB", "MB", "GB"];
    const indice = Math.min(Math.floor(Math.log(valorNumerico) / Math.log(1024)), unidades.length - 1);
    const tamanhoFormatado = valorNumerico / Math.pow(1024, indice);

    return `${tamanhoFormatado.toFixed(indice === 0 ? 0 : 2)} ${unidades[indice]}`;
};

export const mapearCamposOrientacaoCompleta = (dadosAntes: any, dadosDepois: any): CampoOrientacaoCompleta[] => {
    const dadosAntesNormalizados = parseJsonSafely(dadosAntes) || dadosAntes;
    const dadosDepoisNormalizados = parseJsonSafely(dadosDepois) || dadosDepois;

    if (!dadosDepoisNormalizados || typeof dadosDepoisNormalizados !== "object") {
        return [];
    }

    const campos: CampoOrientacaoCompleta[] = [];

    for (const item of CAMPOS_ORDEM) {
        const antes = formatarValor(dadosAntesNormalizados?.[item.key]);
        const depois = formatarValor(dadosDepoisNormalizados[item.key]);
        campos.push({
            campo: labelPorChave[item.key] || item.key,
            antes,
            depois
        });
    }

    return campos;
};

export const mapearAnexosOrientacaoCompleta = (logs: any[]): AnexoOrientacaoCompleta[] => {
    if (!Array.isArray(logs)) {
        return [];
    }

    const anexos: AnexoOrientacaoCompleta[] = [];

    logs
        .filter((log) => log?.entidade_nome === "orientacao_arquivo")
        .forEach((log) => {
            const dados = parseJsonSafely(log.dados_depois) || log.dados_depois;
            const dadosAntes = parseJsonSafely(log.dados_antes) || log.dados_antes;

            if (!dados) {
                return;
            }

            const dadosExtra = parseJsonSafely(log.dados_extra) || log.dados_extra;
            const tamanho = dadosExtra?.tamanho ?? dados?.tamanho ?? dadosAntes?.tamanho ?? null;
            const excluidoAtual = dados.excluido === true || dados.excluido === 1;
            const caminho = dados.caminho || dadosAntes?.caminho || dados.nome;

            anexos.push({
                id: typeof log.entidade_id === "number" ? log.entidade_id : Number(log.entidade_id) || null,
                nome: extrairNomeArquivo(caminho),
                tamanho: formatarTamanho(tamanho),
                nome_responsavel: dados.nome_responsavel || undefined,
                tipo_responsavel: dados.tipo_responsavel || undefined,
                excluido: excluidoAtual
            });
        });

    return anexos;
};

